import React, { useCallback, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { extend, map } from 'underscore';
import { makeStyles, Input, shorthands } from '@fluentui/react-components';
import { StockImageDataService } from '@bingads-webui-campaign/stock-image-data-service';
import { MessageBar } from '@fluentui/react-message-bar';
import { SearchFilled } from '@fluentui/react-icons';
import { PanelImageGrid } from '../panel-image-grid';
import { AdsStudioSpinner } from '../ads-studio-spinner';


const PAGE_SIZE = 20;

const useStyles = makeStyles({
  contentWrapper: {
    paddingTop: '8px',
  },
  searchArea: {
    marginBottom: '16px',
    display: 'flex',
    flexWrap: 'wrap',
  },
  input: {
    width: '260px',
    marginRight: '8px',
    marginBottom: '8px',
  },
  messageBar: {
    marginBottom: '8px',
    backgroundColor: '#EFF6FC',
  },
  dropdown: {
    marginBottom: '8px',
    marginRight: '8px',
    minWidth: 'auto',
  },
  listbox: {
    minWidth: 'auto',
  },
  helpLink: {
    color: '#3073ad',
    ...shorthands.textDecoration('underline'),
    fontWeight: 'normal',
    backgroundColor: 'transparent',
    ...shorthands.borderRadius('0px', '0px', '0px', '0px'),
    ...shorthands.border('none'),
  },
});

export const StockImagePanelContent = (props) => {
  const {
    i18n, aid, cid, oDataConfig, setSelectedImages, overlayBodyRef,
  } = props;
  const classes = useStyles();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoadingFirstPage, setIsLoadingFirstPage] = useState(false);
  const [images, setImages] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const selectedImages = useRef([]);
  const searchQuery = useRef('');
  const skip = useRef(0);
  const hasMore = useRef(true);
  const filterQueryJSON = useRef('{}');

  const onInputChange = useCallback((ev, data) => {
    const { value = '' } = data || {};
    searchQuery.current = value;
  }, []);
  const stockImageService = useMemo(() => new StockImageDataService({
    cid, aid, oDataConfig, controller: 'StockImages',
  }), [oDataConfig, aid, cid]);

  const getStockImages = useCallback(options => stockImageService.getStockImages(options), [stockImageService]);

  /* istanbul ignore next */
  const loadImages = useCallback(({ $search = searchQuery.current, $skip = skip.current, $query = JSON.parse(filterQueryJSON.current) || {} }) => {
    if ($search) {
      if ($skip === 0) {
        selectedImages.current = [];
        setSelectedImages(selectedImages.current);
        setIsLoadingFirstPage(true);
      } else {
        setIsLoadingMore(true);
      }

      getStockImages({
        customQueries: extend({
          appName: 'CampaignUI',
          $search,
          $skip,
          $top: PAGE_SIZE,
          language: 'en',
          includevertical: true,
        }, $query),
      }).then((data) => {
        hasMore.current = data['@odata.count'] > data.value.length + $skip;
        setTotalCount(data['@odata.count'] || 0);

        setImages(prevImages => [
          ...($skip > 0 ? prevImages : []),
          ...map(data.value, (item) => {
            const image = {
              url: item.Preview.Url,
              id: item.Id,
            };

            return image;
          }),
        ]);

        if ($skip === 0) {
          setIsLoadingFirstPage(false);
        } else {
          setIsLoadingMore(false);
        }
      });
    }
  }, [getStockImages, setSelectedImages]);

  /* istanbul ignore next */
  const loadNextPage = useCallback(() => {
    if (hasMore.current) {
      const newSkip = skip.current + PAGE_SIZE;
      skip.current = newSkip;
      loadImages({ $skip: newSkip });
    }
  }, [loadImages]);

  const dataService = useMemo(() => ({
    assets: images,
    totalAssetCount: totalCount,
    loadMoreData: loadNextPage,
  }), [images, loadNextPage, totalCount]);

  const onKeyDown = useCallback((ev) => {
    if (ev.key === 'Enter') {
      skip.current = 0;
      loadImages({
        $skip: 0,
      });
    }
  }, [loadImages]);

  /* istanbul ignore next */
  const onSelectImage = useCallback((imageId) => {
    const originSelectedLength = selectedImages.current.length;
    const newSelectedList = selectedImages.current.filter(({ id }) => id !== imageId);
    if (newSelectedList.length === originSelectedLength) {
      const selectedItem = images.find(({ id }) => id === imageId);
      selectedImages.current.push(selectedItem);
    } else {
      selectedImages.current = newSelectedList;
    }
    setSelectedImages([...selectedImages.current]);
  }, [images, setSelectedImages]);

  return (
    <div className={classNames('stock-content-container', classes.contentWrapper)}>
      <div className={classes.searchArea}>
        <Input
          className={classNames('stock-search-input', classes.input)}
          onChange={onInputChange}
          placeholder={i18n.getString(_TL_('Search for assets'))}
          contentBefore={<SearchFilled />}
          onKeyDown={onKeyDown}
        />
      </div>
      <MessageBar className={classNames('stock-watermark-bar', classes.messageBar)}>
        {i18n.getString(_TL_('The Shutterstock watermark appearing on the stock images below will not appear in your ad.'))}
      </MessageBar>
      <MessageBar className={classNames('stock-ms-bar', classes.messageBar)}>
        <span>
          {i18n.getString(_TL_('There may be images provided by Shutterstock that do not adhere to the Microsoft Advertising image policies.'))}
          <button className={classNames(`HelpTopic ${classes.helpLink}`)} query="help:app54718">{i18n.getString(_TL_('Learn more'))}</button>
        </span>
      </MessageBar>
      { isLoadingFirstPage && !images.length && <AdsStudioSpinner useCustomSize customStyle={{ marginTop: '50px' }} /> }
      {!!images.length && (
        <PanelImageGrid
          i18n={i18n}
          isLoadingMore={isLoadingMore}
          onSelectImage={onSelectImage}
          dataService={dataService}
          overlayBodyRef={overlayBodyRef}
        />
      )}
    </div>
  );
};

StockImagePanelContent.prototype = {
  aid: PropTypes.number.isRequired,
  cid: PropTypes.number.isRequired,
  oDataConfig: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  setSelectedImages: PropTypes.func.isRequired,
};

